import { useEffect, useId, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { GraduationCap, CalendarCheck, CurrencyInr, Notebook, Exam, Cake } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { api, inr, fmtDate } from "../lib/api";
import { useT } from "../lib/i18n";
import { SchoolMark } from "./Photo";
import { welcomeText, quoteOfDay } from "../lib/welcomeText";

const useW = () => {
  const { lang, t } = useT();
  return { w: welcomeText(lang), t, lang, fd: welcomeText(lang).date || fmtDate };
};

export const RoboSparkMark = ({ className = "coin3d-icon" }) => {
  const id = useId().replace(/:/g, "");
  return (
  <svg viewBox="0 0 64 64" className={className} aria-hidden>
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#fde68a" /><stop offset="1" stopColor="#f97316" /></linearGradient>
    </defs>
    <path d="M32 3 L34.2 9.8 L41 12 L34.2 14.2 L32 21 L29.8 14.2 L23 12 L29.8 9.8 Z" fill={`url(#${id})`} />
    <rect x="30.6" y="19" width="2.8" height="7" rx="1.4" fill="#e0f2fe" />
    <rect x="12" y="25" width="40" height="31" rx="11" fill="#eff6ff" />
    <rect x="17" y="31" width="30" height="16" rx="8" fill="#1e3a8a" />
    <circle cx="25.5" cy="39" r="4.6" fill={`url(#${id})`} />
    <circle cx="38.5" cy="39" r="4.6" fill={`url(#${id})`} />
    <circle cx="24.3" cy="37.6" r="1.3" fill="#fff" />
    <circle cx="37.3" cy="37.6" r="1.3" fill="#fff" />
    <rect x="8.5" y="36" width="3.5" height="9" rx="1.75" fill="#bfdbfe" />
    <rect x="52" y="36" width="3.5" height="9" rx="1.75" fill="#bfdbfe" />
  </svg>
  );
};

export const Coin3D = ({ size = "sm", testid }) => (
  <span className={`coin3d coin3d-${size}`} data-testid={testid} aria-label="Schola-AI by RoboSpark">
    <span className="coin3d-spin">
      <span className="coin3d-face"><GraduationCap weight="fill" className="coin3d-icon" /></span>
      <span className="coin3d-face coin3d-back"><RoboSparkMark /></span>
    </span>
  </span>
);

const today = () => new Date().toISOString().slice(0, 10);

export function useWelcome(user) {
  const [show, setShow] = useState(false);
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!sessionStorage.getItem("schola_just_logged_in")) return;
    sessionStorage.removeItem("schola_just_logged_in");
    const key = `schola_welcome_${user.id || user.email}`;
    if (localStorage.getItem(key) !== today()) {
      localStorage.setItem(key, today());
      setShow(true);
    }
    if (user.role !== "superadmin") {
      api.get("/me/welcome").then((r) => {
        setInfo(r.data);
        if (r.data.birthday) setShow(true);
      }).catch(() => {});
    }
  }, [user]);
  useEffect(() => {
    if (!show) return undefined;
    const base = ["admin", "teacher", "student"].includes(user.role) ? 3800 : user.role === "parent" ? 4500 : 3000;
    const t = setTimeout(() => setShow(false), base + (info?.birthday ? 1500 : 0));
    return () => clearTimeout(t);
  }, [show, user.role, info]);
  return [show, () => setShow(false), info];
}

const Pulse = ({ icon: I, label, value, tone, delay, testid }) => (
  <motion.div initial={{ opacity: 0, y: 20, rotateX: -50 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ delay, duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
    className="glass-strong flex min-w-[200px] items-center gap-3 rounded-2xl px-5 py-4 text-left shadow-[0_0_30px_-10px_rgba(59,130,246,0.6)]" data-testid={testid}>
    <span className={`grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5 ${tone}`}><I size={20} weight="duotone" /></span>
    <div>
      <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-400">{label}</div>
      <div className={`font-display text-2xl font-semibold tabular-nums ${tone}`} data-testid={`${testid}-value`}>{value}</div>
    </div>
  </motion.div>
);

function TodayGlance({ show }) {
  const { w } = useW();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (show) api.get("/dashboard/admin").then((r) => setD(r.data)).catch(() => {});
  }, [show]);
  if (!d) return <div className="mt-8 h-[76px]" />;
  return (
    <div className="mt-8 flex flex-col gap-3 sm:flex-row" style={{ perspective: 800 }} data-testid="welcome-glance">
      <Pulse icon={CalendarCheck} label={w.todayAtt} value={d.today_attendance != null ? `${d.today_attendance}%` : w.notMarked} tone="text-emerald-300" delay={0.9} testid="welcome-attendance" />
      <Pulse icon={CurrencyInr} label={w.pendingFees(d.pending_invoices)} value={inr(d.fee_pending)} tone="text-orange-300" delay={1.05} testid="welcome-pending" />
    </div>
  );
}

function TeacherGlance({ show }) {
  const { w } = useW();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (show) api.get("/dashboard/teacher").then((r) => setD(r.data)).catch(() => {});
  }, [show]);
  if (!d) return <div className="mt-8 h-[76px]" />;
  return (
    <div className="mt-8 flex flex-col flex-wrap justify-center gap-3 sm:flex-row" style={{ perspective: 800 }} data-testid="welcome-teacher-glance">
      {d.my_classes.length ? d.my_classes.slice(0, 3).map((c, i) => (
        <Pulse key={c.class_id} icon={CalendarCheck} label={w.today(w.cls(c.class_name))} delay={0.9 + i * 0.12} testid={`welcome-class-${i}`}
          value={c.marked ? `${c.percent ?? 0}% · ${c.present}/${c.total}` : w.notMarked} tone={c.marked ? "text-emerald-300" : "text-amber-200"} />
      )) : (
        <Pulse icon={CalendarCheck} label={w.markedToday} value={w.ofClasses(d.attendance_marked_today, d.classes)} tone="text-emerald-300" delay={0.9} testid="welcome-class-0" />
      )}
    </div>
  );
}

function ParentGlance({ show, onSkip }) {
  const nav = useNavigate();
  const { w, t, fd } = useW();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (show) api.get("/my/overview").then((r) => setD(r.data)).catch(() => {});
  }, [show]);
  if (!d) return <div className="mt-8 h-[76px]" />;
  const st = { P: [w.present, "text-emerald-300"], L: [w.late, "text-amber-200"], A: [w.absent, "text-rose-300"] };
  return (
    <div className="mt-8 flex flex-col gap-3" style={{ perspective: 800 }} data-testid="welcome-parent-glance">
      {d.children.slice(0, 2).map((k, i) => {
        const [txt, tone] = st[k.today_status] || [w.notMarked, "text-zinc-300"];
        const due = k.next_due;
        return (
          <div key={k.id} className="flex flex-col gap-3 sm:flex-row">
            <Pulse icon={CalendarCheck} label={`${k.name.split(" ")[0]} · ${w.cls(k.class_name)}`} value={txt} tone={tone} delay={0.9 + i * 0.2} testid={`welcome-child-${i}-attendance`} />
            <Pulse icon={CurrencyInr} label={due ? `${due.due_date && due.due_date < new Date().toISOString().slice(0, 10) ? w.overdue : w.nextFee} · ${t(due.title)}` : w.fees} tone={due ? "text-orange-300" : "text-emerald-300"} delay={1.02 + i * 0.2} testid={`welcome-child-${i}-fee`}
              value={due ? `${inr(due.balance)} · ${due.due_date ? fd(due.due_date) : w.noDate}` : w.allPaid} />
          </div>
        );
      })}
      {d.children.some((k) => k.next_due) && (
        <motion.button initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.3, duration: 0.5 }}
          onClick={(e) => { e.stopPropagation(); onSkip(); nav("/fees"); }} data-testid="welcome-pay-now-btn"
          className="mx-auto mt-2 inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-2.5 text-sm font-semibold text-white shadow-[0_8px_30px_-6px_rgba(249,115,22,0.8)] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-orange-400">
          <CurrencyInr size={16} weight="bold" /> {w.payNow}
        </motion.button>
      )}
    </div>
  );
}

function StudentGlance({ show }) {
  const { w, t, fd } = useW();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (show) api.get("/my/overview").then((r) => setD(r.data)).catch(() => {});
  }, [show]);
  const k = d?.children?.[0];
  if (!k) return <div className="mt-8 h-[76px]" />;
  const hw = k.homework[0];
  return (
    <div className="mt-8 flex flex-col gap-3 sm:flex-row" style={{ perspective: 800 }} data-testid="welcome-student-glance">
      <Pulse icon={Notebook} label={hw ? `${w.homework} · ${t(hw.subject)} · ${w.due} ${fd(hw.due_date)}` : w.homework} tone={hw ? "text-blue-300" : "text-emerald-300"}
        value={hw ? `${hw.title}${k.homework.length > 1 ? ` +${k.homework.length - 1}` : ""}` : w.nothingDue} delay={0.9} testid="welcome-homework" />
      <Pulse icon={Exam} label={w.nextExam} tone="text-orange-300" delay={1.02} testid="welcome-exam"
        value={k.next_exam ? `${t(k.next_exam.name)} · ${fd(k.next_exam.date)}` : w.noneScheduled} />
    </div>
  );
}

const COLORS = ["#60a5fa", "#f97316", "#fbbf24", "#f472b6", "#34d399", "#a78bfa"];
const PIECES = Array.from({ length: 70 }, (_, i) => ({ x: (i * 37) % 100, d: (i % 7) * 0.18, s: 6 + (i % 4) * 3, r: (i * 53) % 360, c: COLORS[i % COLORS.length], round: i % 3 === 0 }));

const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="welcome-confetti">
    {PIECES.map((p, i) => (
      <motion.span key={i} className="absolute top-0 block" style={{ left: `${p.x}%`, width: p.s, height: p.round ? p.s : p.s * 0.45, background: p.c, borderRadius: p.round ? 9999 : 2 }}
        initial={{ y: -40, rotate: p.r, opacity: 0 }} animate={{ y: "105vh", rotate: p.r + 540, opacity: [0, 1, 1, 0.8] }}
        transition={{ delay: 0.3 + p.d, duration: 3.2 + (i % 5) * 0.4, ease: "easeIn" }} />
    ))}
  </div>
);

export function WelcomeIntro({ show, onSkip, user, info }) {
  const first = (user.name || "").split(" ").filter((w) => !/^(dr|mr|mrs|ms)\.?$/i.test(w))[0] || "";
  const { w, lang } = useW();
  const q = quoteOfDay(lang, info?.quotes);
  const bday = !!info?.birthday;
  return (
    <AnimatePresence>
      {show && (
        <motion.div key="welcome" onClick={onSkip} data-testid="welcome-intro" data-no-translate=""
          className="fixed inset-0 z-[100] grid cursor-pointer place-items-center overflow-hidden bg-[#05050a]" style={{ perspective: 1200 }}
          initial={{ opacity: 1 }} exit={{ opacity: 0, scale: 1.25, rotateX: 18, filter: "blur(6px)" }} transition={{ duration: 0.6, ease: [0.7, 0, 0.3, 1] }}>
          <div className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-600/25 blur-[120px]" />
          <div className="absolute -bottom-40 -right-40 h-[380px] w-[380px] rounded-full bg-orange-500/10 blur-[120px]" />
          {bday && <Confetti />}
          <div className="relative flex flex-col items-center text-center">
            <motion.div initial={{ scale: 0.3, rotateY: -540, opacity: 0, z: -400 }} animate={{ scale: 1, rotateY: 0, opacity: 1, z: 0 }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} style={{ transformStyle: "preserve-3d" }}>
              <Coin3D size="lg" />
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24, rotateX: -40 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ delay: 0.45, duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
              className="mt-10 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white" data-testid="welcome-intro-title">
              {bday ? w.birthday(first) : w.welcome(first)}
            </motion.h1>
            {bday && (
              <motion.p initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.75, type: "spring", stiffness: 200 }}
                className="mt-3 inline-flex items-center gap-2 text-base text-amber-200" data-testid="welcome-birthday">
                <Cake size={20} weight="duotone" /> {w.bdayWish}
              </motion.p>
            )}
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }} className="mt-3 flex items-center gap-2 text-sm text-zinc-400">
              <SchoolMark school={user.school} className="h-8 w-8 rounded-lg bg-white p-0.5" testid="welcome-school-logo" />
              {user.school?.name || w.platform}
            </motion.p>
            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.5, duration: 1.3, ease: "easeInOut" }}
              className="mt-8 h-[2px] w-40 origin-left rounded-full bg-gradient-to-r from-blue-500 to-orange-400" />
            {user.role === "admin" && <TodayGlance show={show} />}
            {user.role === "teacher" && <TeacherGlance show={show} />}
            {user.role === "parent" && <ParentGlance show={show} onSkip={onSkip} />}
            {user.role === "student" && <StudentGlance show={show} />}
            <motion.figure initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.7 }}
              className="mt-8 max-w-xl px-6" data-testid="welcome-quote">
              <div className="text-[10px] uppercase tracking-[0.25em] text-blue-300/80">{w.thought}</div>
              <blockquote className="mt-2 font-display text-base sm:text-lg italic leading-relaxed text-zinc-200" data-testid="welcome-quote-text">“{q.text}”</blockquote>
              <figcaption className="mt-1.5 text-xs text-zinc-500">{q.by && `— ${q.by}`}</figcaption>
            </motion.figure>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
